<template>
    <div
        class="main-layout"
        :class="{
            'sidebar-hidden': !sidebarExpanded,
            'dark-theme': isDarkTheme,
            'light-theme': !isDarkTheme,
        }"
    >
        <a href="#" @click="skipMainContent" class="skip-main-content">
            <span>{{ $t("Skip to main content") }}</span>
        </a>
        <div id="global-teleport-container"></div>
        <TopBar @toggle-menu="toggleMenu" @open-modal="openChatAssistant"></TopBar>
        <MenuSideBar v-model:expanded="sidebarExpanded"></MenuSideBar>
        <div v-if="sidebarExpanded" class="side-bar-overlay" @click="closeSideBar"></div>
        <AuthLoadingOverlay :display="loadingAuth"></AuthLoadingOverlay>

        <OnboardingWizard v-if="onboardingOpen" @finished="closeOnboarding" @dismissed="closeOnboarding"></OnboardingWizard>

        <router-view v-if="!loadingAuth"></router-view>
        <LoadingOverlay v-if="loadingRoute"></LoadingOverlay>

        <ProfileModal v-if="displayProfileModal" v-model:display="displayProfileModal" :uid="profileModalUser"> </ProfileModal>

        <ActionModal
            v-if="displayActionModal"
            v-model:display="displayActionModal"
            :only-component="actionOnlyComponent"
            :modal-type="actionModalType"
            :title="actionTitle"
            :message="actionMessage"
            :message-button="actionMessageButton"
            :icon-button="actionIconButton"
            :alert-type="actionAlertType"
            :alert-position="actionPosition"
            :alert-message="actionAlertMessage"
            :custom-component="actionCustomComponent"
            :component-props="actionComponentProps"
            :position-icon-header="actionPositionIconHeader"
            :display-footer="actionDisplayFooter"
            :display-close-button="actionDisplayCloseButton"
            :steps="actionSteps"
            :max-width="actionModalMaxWidth"
            :full-height="actionModalFullHeight"
            :static-dialog="actionModalStaticDialog"
            @confirm="onActionConfirm"
        >
        </ActionModal>

        <ActionModal
            v-for="(modal, index) in actionModalStack"
            :key="modal.id"
            :display="modal.display"
            :only-component="modal.onlyComponent"
            :modal-type="modal.modalType"
            :title="modal.title"
            :message="modal.message"
            :message-button="modal.messageButton"
            :icon-button="modal.iconButton"
            :alert-type="modal.alertType"
            :alert-position="modal.alertPosition"
            :alert-message="modal.alertMessage"
            :custom-component="modal.customComponent"
            :component-props="modal.componentProps"
            :position-icon-header="modal.positionIconHeader"
            :display-footer="modal.displayFooter"
            :display-close-button="modal.displayCloseButton"
            :steps="modal.steps"
            :max-width="modal.maxWidth"
            :full-height="modal.fullHeight"
            :static-dialog="modal.staticDialog"
            :z-index="getStackActionModalZIndex(index)"
            @update:display="onStackActionModalDisplay(modal.id, $event)"
            @confirm="onStackActionConfirm(modal.id, $event)"
        >
        </ActionModal>

        <ConfirmationModal
            v-if="displayConfirmationModal"
            v-model:display="displayConfirmationModal"
            :title="confirmationTitle"
            :message="confirmationMessage"
            :danger="confirmationDanger"
            @confirm="onConfirm"
        >
        </ConfirmationModal>

        <MessageModal
            v-if="displayModalMessage"
            v-model:display="displayModalMessage"
            :title="messageModalTitle"
            :message="messageModalMessage"
        ></MessageModal>

        <WalletConfirmationModal
            v-if="displayWalletConfirmationModal"
            v-model:display="displayWalletConfirmationModal"
            :title="confirmationTitle"
            :message="confirmationMessage"
            :danger="confirmationDanger"
            @confirm="onConfirmWallet"
        >
        </WalletConfirmationModal>

        <SnackBar></SnackBar>

        <NotificationAlertStack />

        <ChatAssistantWidget v-if="displayAssistantWidget" v-model:display="displayAssistantWidget"></ChatAssistantWidget>
    </div>
</template>

<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, markRaw } from "vue";

import TopBar from "@/components/layout/TopBar.vue";
import MenuSideBar from "@/components/layout/MenuSideBar.vue";
import LoadingOverlay from "@/components/layout/LoadingOverlay.vue";
import AuthLoadingOverlay from "@/components/layout/AuthLoadingOverlay.vue";
import SnackBar from "@/components/layout/SnackBar.vue";
import NotificationAlertStack from "@/components/layout/NotificationAlertStack.vue";

import ProfileModal from "@/components/modals/ProfileModal.vue";

import DataSpaceParticipantModalContent from "@/components/modals/DataSpaceParticipantModalContent.vue";
import AttestationViewModalContent from "@/components/modals/AttestationViewModalContent.vue";
import DidDocumentModalContent from "@/components/modals/DidDocumentModalContent.vue";

import MessageModal from "@/components/modals/MessageModal.vue";

import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import WalletConfirmationModal from "../modals/WalletConfirmationModal.vue";

import ComponentLoader from "../utils/ComponentLoader.vue";

import { ApiRulebook } from "@/api/api-group-rulebook";
import { AuthController } from "@/control/auth";
import { getTheme } from "@/control/app-preferences";
import { DataSpaceConfigurationChecker } from "@/control/ds-config-checker";
import { hasMandatoryStepPending } from "@/utils/enums/data-space-config";

import ActionModal from "@/components/modals/ActionModal.vue";
import type { ActionModalOptions, ActionModalStep, ActionModalType } from "@/types/action-modal";
import { getUniqueStringId } from "@/utils/unique-id";
import { getToolArgument } from "@/utils/llm-tools";
import { Request } from "@asanrom/request-browser";
import { ApiDatasets } from "@/api/api-group-datasets";
import { ApiCatalog } from "@/api/api-group-catalog";
import { ApiSources } from "@/api/api-group-sources";
import { ApiSinks } from "@/api/api-group-sinks";

const OnboardingWizard = defineAsyncComponent({
    loader: () => import("@/components/onboarding/OnboardingWizard.vue"),
    loadingComponent: ComponentLoader,
    delay: 1000,
});

const ChatAssistantWidget = defineAsyncComponent({
    loader: () => import("@/components/widgets/ChatAssistantWidget.vue"),
    loadingComponent: ComponentLoader,
    delay: 1000,
});

function safeStringify(value: unknown): string {
    try {
        return JSON.stringify(value) ?? "";
    } catch {
        return "";
    }
}

interface ActionModalStackItem {
    id: number;
    display: boolean;
    onlyComponent: boolean;
    modalType: ActionModalType;
    title?: string;
    message: string;
    messageButton: string;
    iconButton: string;
    alertType: "alert-info" | "alert-success" | "alert-danger" | "alert-warning" | "none";
    alertPosition: "top" | "bottom" | "middle";
    alertMessage: string;
    customComponent: any;
    componentProps: any;
    positionIconHeader: "left" | "center" | "right" | "none";
    displayFooter: boolean;
    displayCloseButton: boolean;
    steps: ActionModalStep[];
    maxWidth?: number;
    fullHeight: boolean;
    staticDialog: boolean;
    callback: null | ((sharedData?: Record<string, any>) => void);
}

export default defineComponent({
    components: {
        TopBar,
        MenuSideBar,
        LoadingOverlay,
        AuthLoadingOverlay,
        SnackBar,
        NotificationAlertStack,
        MessageModal,
        ConfirmationModal,
        ProfileModal,
        WalletConfirmationModal,
        ActionModal,
        ChatAssistantWidget,
        OnboardingWizard,
    },
    name: "MainLayout",
    setup: function () {
        return {
            confirmationCallback: null,
            resizeObserver: null as ResizeObserver,
            assistantSearchRequestId: getUniqueStringId(),
            rulebookInstructionsRequestId: getUniqueStringId(),
        };
    },
    data() {
        return {
            loadingRoute: false,

            rulebookInstructions: "",

            sidebarExpanded: false,
            sidebarUserPreference: false,
            sidebarSticky: true,

            isDarkTheme: false,
            loadingAuth: AuthController.Loading && !AuthController.FirstTimeLoaded,

            displayModalMessage: false,
            messageModalTitle: "",
            messageModalMessage: "",

            displayConfirmationModal: false,
            confirmationTitle: "",
            confirmationMessage: "",
            confirmationDanger: false,

            displayWalletConfirmationModal: false,

            displayProfileModal: false,
            profileModalUser: "",

            dataSpaceConfigurationMissing: !DataSpaceConfigurationChecker.ConfigCheckStatus.configured,

            // The onboarding is a first-run flow, not a live view of the configuration status:
            // once it opens it stays mounted until the admin finishes or exits it, and once it
            // is closed it does not come back. Deriving it from the check on every update would
            // unmount it as soon as the last step is saved (hiding its completion screen) and
            // would bring it back on top of the whole app the next time something breaks
            onboardingOpen: false,
            onboardingClosed: false,
            canSeeOnboarding: AuthController.isAuthenticated() && AuthController.hasPermission("admin.ds"),

            // ActionModal props
            displayActionModal: false,
            actionOnlyComponent: false,
            actionModalType: "danger" as ActionModalType,
            actionTitle: "",
            actionMessage: "",
            actionMessageButton: "",
            actionIconButton: "fas fa-check",
            actionAlertType: "none" as "alert-info" | "alert-success" | "alert-danger" | "alert-warning" | "none",
            actionPosition: "top" as "top" | "bottom" | "middle",
            actionAlertMessage: "Are you sure you want to proceed? This action cannot be undone.",
            actionCustomComponent: null as any,
            actionComponentProps: {} as any,
            actionPositionIconHeader: "left" as "left" | "center" | "right" | "none",
            actionDisplayFooter: true,
            actionDisplayCloseButton: false,
            actionSteps: [] as any[],
            actionModalMaxWidth: undefined as number | undefined,
            actionModalFullHeight: false,
            actionModalStaticDialog: false,
            actionCallback: null as null | ((sharedData?: Record<string, any>) => void),
            actionModalStack: [] as ActionModalStackItem[],
            nextActionModalStackId: 1,

            displayAssistantWidget: false,
        };
    },
    methods: {
        loadRulebookInstructions: function () {
            Request.Abort(this.rulebookInstructionsRequestId);

            if (!AuthController.isAuthenticated()) {
                this.rulebookInstructions = "";
                return;
            }

            Request.Pending(this.rulebookInstructionsRequestId, ApiRulebook.ListActiveRulebooks({ page: 1, pageSize: 64 }))
                .onSuccess((res) => {
                    const parts = res.list
                        .filter((rb) => rb.origin === "LOCAL" && !!rb.promptAddendum)
                        .map((rb) => `From the rulebook "${rb.name}" ${rb.version}:\n${rb.promptAddendum}`);

                    this.rulebookInstructions = parts.length
                        ? "The administrator of this data space gives you these extra instructions, on top of the rulebook itself. " +
                          "Follow them when they apply, and say so when you do:\n\n" +
                          parts.join("\n\n")
                        : "";
                })
                .onRequestError(() => {
                    this.rulebookInstructions = "";
                })
                .onUnexpectedError(() => {
                    this.rulebookInstructions = "";
                });
        },
        closeSideBar: function () {
            this.sidebarExpanded = false;
            this.sidebarUserPreference = false;
        },

        onAuthLoadingChanged: function () {
            this.loadingAuth = AuthController.Loading && !AuthController.FirstTimeLoaded;
        },

        onThemeChanged: function () {
            this.isDarkTheme = getTheme() === "dark";
        },

        toggleMenu: function () {
            this.sidebarExpanded = !this.sidebarExpanded;
            if (window.innerWidth >= 1000) {
                this.sidebarUserPreference = this.sidebarExpanded;
            }
        },

        skipMainContent: function (event) {
            if (event) {
                event.preventDefault();
            }
            const content: any = document.querySelector(".page-content");
            if (content) {
                content.focus();
            }
        },

        showMessage: function (title: string, msg: string) {
            this.messageModalTitle = title;
            this.messageModalMessage = msg;
            this.displayModalMessage = true;
        },

        openChatAssistant: function () {
            this.displayAssistantWidget = !this.displayAssistantWidget;
        },

        onRouterLoading: function (l: boolean) {
            this.loadingRoute = l;
        },

        onRouteChanged: function () {
            if (this.$route && this.$route.meta && typeof this.$route.meta === "object") {
                this.sidebarSticky = !!this.$route.meta.sidebarSticky;
            } else {
                this.sidebarSticky = false;
            }

            if (window.innerWidth < 1000) {
                this.sidebarExpanded = false;
            }
        },

        showConfirmationModal: function (options: {
            title: string;
            message: string;
            danger?: boolean;
            askWalletPassword?: boolean;
            callback: (pw?: string) => void;
        }) {
            if (options.askWalletPassword) {
                this.displayWalletConfirmationModal = true;
            } else {
                this.displayConfirmationModal = true;
            }

            this.confirmationTitle = options.title;
            this.confirmationMessage = options.message;
            this.confirmationDanger = !!options.danger;
            this.confirmationCallback = options.callback;
        },

        onConfirm: function () {
            if (this.confirmationCallback) {
                this.confirmationCallback();
            }
        },

        onConfirmWallet: function (password: string) {
            if (this.confirmationCallback) {
                this.confirmationCallback(password);
            }
        },

        onShowProfile: function (uid: string) {
            this.profileModalUser = uid;
            this.displayProfileModal = true;
        },

        onShowParticipant: function (id: string) {
            // Three steps of the same dialog, so the certificate and the DID document go back to the
            // participant instead of closing it. The old modal did it with a router-link, which also
            // navigated and threw the user out of the tab they were in
            this.$openModal({
                steps: [
                    {
                        id: "participant-details",
                        title: this.$t("Participant details"),
                        modalType: "info",
                        positionIconHeader: "none",
                        maxWidth: 800,
                        displayCloseButton: false,
                        component: markRaw(DataSpaceParticipantModalContent),
                        componentProps: {
                            participantId: id,
                        },
                    },
                    {
                        id: "attestation-view-modal",
                        title: this.$t("Attestation details"),
                        modalType: "info",
                        positionIconHeader: "none",
                        displayFooter: true,
                        maxWidth: 800,
                        component: markRaw(AttestationViewModalContent),
                    },
                    {
                        id: "document-did-modal",
                        title: this.$t("DID Document"),
                        modalType: "info",
                        positionIconHeader: "none",
                        displayFooter: true,
                        maxWidth: 800,
                        component: markRaw(DidDocumentModalContent),
                    },
                ],
            });
        },

        onPageResize: function () {
            this.sidebarExpanded = window.innerWidth >= 1000 ? this.sidebarUserPreference : false;
        },

        // Opens the wizard the first time an admin meets an unconfigured data space that has
        // not retired the onboarding yet. It never re-opens it on its own afterwards
        refreshOnboardingGate: function () {
            if (!this.canSeeOnboarding) {
                // Logged out or lost the permission: drop the wizard and let the next admin
                // that logs in on this tab be evaluated from scratch
                this.onboardingOpen = false;
                this.onboardingClosed = false;
                return;
            }

            if (this.onboardingOpen || this.onboardingClosed) {
                return;
            }

            // Until the check has actually answered, the stored status is the optimistic
            // default and nothing can be decided from it
            if (!DataSpaceConfigurationChecker.Loaded) {
                return;
            }

            const status = DataSpaceConfigurationChecker.ConfigCheckStatus;

            // Taking over the whole screen belongs to the onboarding phase, and that phase is
            // over as soon as the data space works. Two ways to know it does: everything is in
            // order, or the only thing left is optional, which the admin may simply never have
            // opted into. An installation upgrading from a version without the dismissal marker
            // reports it as unset, so without this the assistant would seize a connector that
            // had been running for months the first time anything drifted. From here on the
            // banner is what warns, and its Configure button still leads into the assistant.
            if (status.configured || !hasMandatoryStepPending(status.steps)) {
                this.onboardingClosed = true;
                return;
            }

            this.onboardingOpen = !status.onboardingDismissed;
        },

        // Either the admin finished it (the wizard retired it on the server) or chose to
        // continue later (nothing was retired, it just steps aside for this session)
        closeOnboarding: function () {
            this.onboardingOpen = false;
            this.onboardingClosed = true;
            DataSpaceConfigurationChecker.Load();
        },

        // Opened on demand from the banner or the user menu. Unlike the automatic first-run
        // opening, this ignores the gate: the assistant is the only place where every step can
        // be resolved, so an admin must be able to reach it whenever they ask for it
        resumeOnboarding: function () {
            if (!this.canSeeOnboarding) {
                return;
            }

            this.onboardingClosed = false;
            this.onboardingOpen = true;
        },

        onActionConfirm: function (sharedData?: Record<string, any>) {
            if (this.actionCallback) {
                this.actionCallback(sharedData);
            }
        },

        createStackActionModal: function (options: ActionModalOptions): ActionModalStackItem {
            return {
                id: this.nextActionModalStackId++,
                display: true,
                onlyComponent: options.onlyComponent ?? false,
                modalType: options.modalType || "danger",
                title: options.title,
                message: options.message || "",
                messageButton: options.messageButton || "",
                iconButton: options.iconButton || "fas fa-check",
                alertType: options.alertType || "none",
                alertPosition: options.alertPosition || "top",
                alertMessage: options.alertMessage || "Are you sure you want to proceed? This action cannot be undone.",
                customComponent: options.customComponent || null,
                componentProps: options.componentProps || {},
                positionIconHeader: options.positionIconHeader || "left",
                displayFooter: options.displayFooter !== undefined ? options.displayFooter : true,
                displayCloseButton: options.displayCloseButton ?? false,
                steps: options.steps || [],
                maxWidth: options.maxWidth,
                fullHeight: options.fullHeight ?? false,
                staticDialog: options.staticDialog ?? false,
                callback: options.callback ?? null,
            };
        },

        getStackActionModalZIndex: function (index: number): number {
            return 260 + index * 10;
        },

        onStackActionModalDisplay: function (id: number, display: boolean) {
            const index = this.actionModalStack.findIndex((modal) => modal.id === id);

            if (index === -1) {
                return;
            }

            if (display) {
                this.actionModalStack[index].display = true;
                return;
            }

            // Defer the removal so a synchronously-following `confirm` can still
            // resolve its callback against a modal that is still present in the stack.
            this.$nextTick(() => {
                const removeIndex = this.actionModalStack.findIndex((modal) => modal.id === id);

                if (removeIndex !== -1) {
                    this.actionModalStack.splice(removeIndex, 1);
                }
            });
        },

        onStackActionConfirm: function (id: number, sharedData?: Record<string, any>) {
            const callback = this.actionModalStack.find((m) => m.id === id)?.callback;

            if (callback) {
                callback(sharedData);
            }
        },

        // Repeated identical errors (e.g. a double submit failing twice) would pile up otherwise
        isDuplicateOfTopStackActionModal: function (candidate: ActionModalStackItem): boolean {
            const top = this.actionModalStack[this.actionModalStack.length - 1];

            if (!top || !top.display || top.customComponent !== candidate.customComponent || top.callback !== candidate.callback) {
                return false;
            }

            if (top.steps.length > 0 || candidate.steps.length > 0 || top.title !== candidate.title || top.message !== candidate.message) {
                return false;
            }

            const topProps = safeStringify(top.componentProps);
            const candidateProps = safeStringify(candidate.componentProps);

            return topProps !== "" && topProps === candidateProps;
        },

        showActionModal: function (options: ActionModalOptions) {
            if (options.stack) {
                const candidate = this.createStackActionModal(options);

                if (this.isDuplicateOfTopStackActionModal(candidate)) {
                    return;
                }

                this.actionModalStack.push(candidate);
                return;
            }

            this.actionOnlyComponent = options.onlyComponent ?? false;
            this.actionModalType = options.modalType || "danger";
            this.actionTitle = options.title;
            this.actionMessage = options.message || "";
            this.actionMessageButton = options.messageButton || "";
            this.actionIconButton = options.iconButton || "fas fa-check";
            this.actionAlertType = options.alertType || "none";
            this.actionPosition = options.alertPosition || "top";
            this.actionAlertMessage = options.alertMessage || "Are you sure you want to proceed? This action cannot be undone.";
            this.actionCustomComponent = options.customComponent || null;
            this.actionComponentProps = options.componentProps || {};
            this.actionPositionIconHeader = options.positionIconHeader || "left";
            this.actionDisplayFooter = options.displayFooter !== undefined ? options.displayFooter : true;
            this.actionDisplayCloseButton = options.displayCloseButton ?? false;
            this.actionSteps = options.steps || [];
            this.actionModalMaxWidth = options.maxWidth;
            this.actionModalFullHeight = options.fullHeight ?? false;
            this.actionModalStaticDialog = options.staticDialog ?? false;
            this.actionCallback = options.callback ?? null;

            this.displayActionModal = true;
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-loading", this.onAuthLoadingChanged.bind(this));

        this.$listenOnAppEvent("rulebook-changed", this.loadRulebookInstructions.bind(this));
        this.loadRulebookInstructions();

        this.$listenOnAppEvent("theme-changed", this.onThemeChanged.bind(this));
        this.onThemeChanged();

        this.$listenOnAppEvent("msg-modal", this.showMessage.bind(this));
        this.$listenOnAppEvent("ask-confirmation", this.showConfirmationModal.bind(this));
        this.$listenOnAppEvent("show-profile", this.onShowProfile.bind(this));
        this.$listenOnAppEvent("show-participant", this.onShowParticipant.bind(this));

        this.$listenOnAppEvent("ds-config-check-updated", () => {
            this.dataSpaceConfigurationMissing = !DataSpaceConfigurationChecker.ConfigCheckStatus.configured;
            this.refreshOnboardingGate();
        });

        this.$listenOnAppEvent("auth-status-changed", () => {
            this.canSeeOnboarding = AuthController.isAuthenticated() && AuthController.hasPermission("admin.ds");
            this.refreshOnboardingGate();

            this.loadRulebookInstructions();
        });

        this.$listenOnAppEvent("onboarding-open", this.resumeOnboarding.bind(this));

        // The check may have already resolved before this layout was mounted, in which case
        // its event is long gone and only the stored status is left to evaluate
        this.refreshOnboardingGate();

        this.$listenOnAppEvent("router-load-state-change", this.onRouterLoading.bind(this));

        this.$listenOnAppEvent("action-modal-open", this.showActionModal.bind(this));

        this.$listenOnAppEvent("llm-chat-assistant-open", () => {
            this.displayAssistantWidget = true;
        });

        this.onRouteChanged();

        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(this.onPageResize.bind(this));
            this.resizeObserver.observe(this.$el);
        }

        this.$setDynamicLlmContext(
            computed(() => {
                const contextIds = ["general", "datasets", "catalogs", "sources", "sinks", "rulebook"];

                const layoutWarnings = [
                    this.dataSpaceConfigurationMissing && !this.onboardingOpen
                        ? "It seems the data space is not properly configured. " +
                          "It is recommended to open the setup assistant to complete or fix the configuration. " +
                          "In order to do that, the user must click the **Configure** button on the notification banner."
                        : null,
                ]
                    .filter((a) => !!a)
                    .join("\n");

                const assetsContext =
                    "The user is asking about information stored in the connector. " +
                    "If the user asks you to show a list of the datasets or sources, for example, " +
                    "use the provided tools to provide the user a list of the information that he asked for.";

                const combinedExtraContext = [layoutWarnings, assetsContext, this.rulebookInstructions].filter(Boolean).join("\n\n");
                return {
                    contextIds: contextIds,
                    extraContext: combinedExtraContext,

                    tools: [
                        {
                            name: "FindDatasets",
                            description:
                                "Searches datasets in the connector database. Use it when the user asks about existing datasets, " +
                                "local/remote datasets, dataset names, keywords, themes, creators or catalogs.",
                            parameters: [
                                { name: "title", description: "Optional title text filter." },
                                { name: "local", description: "Use 'true' for local datasets, 'false' for remote datasets." },
                                { name: "keywords", description: "Comma-separated keywords." },
                                { name: "themes", description: "Comma-separated themes." },
                                { name: "catalog", description: "Catalog ID filter." },
                            ],
                            invoke: async (call) => {
                                const title = (getToolArgument(call.arguments, ["title"]) || "") + "";
                                const local = getToolArgument(call.arguments, ["local"]);
                                const keywords = (getToolArgument(call.arguments, ["keywords"]) || "") + "";
                                const themes = (getToolArgument(call.arguments, ["themes"]) || "") + "";
                                const catalog = (getToolArgument(call.arguments, ["catalog"]) || "") + "";

                                return new Promise<string>((resolve, reject) => {
                                    const queryParams: any = {};
                                    if (title) queryParams.title = title;
                                    if (local) queryParams.local = local;
                                    if (keywords) queryParams.keywords = keywords;
                                    if (themes) queryParams.themes = themes;
                                    if (catalog) queryParams.catalog = catalog;

                                    Request.Pending(this.assistantSearchRequestId, ApiDatasets.FindDatasets(queryParams))
                                        .onSuccess((res) => {
                                            if (!res || !res.list || res.list.length === 0) {
                                                resolve("No datasets found matching the specified criteria in the connector database.");
                                                return;
                                            }

                                            const datasetsSummary = res.list
                                                .map((dataset) => {
                                                    return (
                                                        `- **ID:** \`${dataset.id}\`\n` +
                                                        `  **Title:** ${dataset.title}\n` +
                                                        `  **Origin:** ${dataset.local ? "Local" : "Remote"}\n` +
                                                        `  **Catalog:** ${dataset.catalog?.name || dataset.catalog?.id || "N/A"}\n` +
                                                        `  **Keywords:** ${dataset.keywords?.join(", ") || "None"}\n` +
                                                        `  **Themes:** ${dataset.themes?.join(", ") || "None"}\n` +
                                                        `  **Creator:** ${dataset.creator || "Unknown"}\n` +
                                                        `  **Description:** ${dataset.description || "No description available."}`
                                                    );
                                                })
                                                .join("\n\n");

                                            const continuationNotice = res.continuationToken
                                                ? `\n\n*(Note: There are more items available. Continuation token: ${res.continuationToken})*`
                                                : "";

                                            resolve(
                                                `Found ${res.list.length} datasets matching the criteria:\n\n${datasetsSummary}${continuationNotice}`,
                                            );
                                        })
                                        .onRequestError((err, handleError) => {
                                            handleError(err, {
                                                unauthorized: () => reject(new Error("API error: Unauthorized access to datasets.")),
                                                forbidden: () => reject(new Error("API error: Access denied. Missing permission.")),
                                                serverError: () => reject(new Error("API error: Internal server error.")),
                                                temporalError: () => reject(new Error("API error: Temporary connection problem.")),
                                                networkError: () => reject(new Error("Network error: Could not reach the server.")),
                                            });
                                        })
                                        .onUnexpectedError((err) => {
                                            reject(err);
                                        });
                                });
                            },
                        },
                        {
                            name: "FindCatalogs",
                            description:
                                "Searches catalogs in the connector database. Use it when the user asks about available catalogs, " +
                                "wants to filter catalogs by title, creator, origin (local/remote), keywords, themes or participant.",
                            parameters: [
                                { name: "title", description: "Optional title or creator text filter." },
                                { name: "local", description: "Use 'true' for local catalogs, 'false' for remote catalogs." },
                                { name: "creator", description: "Optional exact creator name filter." },
                                { name: "keywords", description: "Comma-separated keywords filter." },
                                { name: "themes", description: "Comma-separated themes filter." },
                                { name: "participant", description: "Optional participant ID provider filter." },
                            ],
                            invoke: async (call) => {
                                const title = (getToolArgument(call.arguments, ["title"]) || "") + "";
                                const local = getToolArgument(call.arguments, ["local"]);
                                const creator = (getToolArgument(call.arguments, ["creator"]) || "") + "";
                                const keywords = (getToolArgument(call.arguments, ["keywords"]) || "") + "";
                                const themes = (getToolArgument(call.arguments, ["themes"]) || "") + "";
                                const participant = (getToolArgument(call.arguments, ["participant"]) || "") + "";

                                return new Promise<string>((resolve, reject) => {
                                    const queryParams: any = {};
                                    if (title) queryParams.title = title;
                                    if (local !== undefined && local !== null) queryParams.local = local + "";
                                    if (creator) queryParams.creator = creator;
                                    if (keywords) queryParams.keywords = keywords;
                                    if (themes) queryParams.themes = themes;
                                    if (participant) queryParams.participant = participant;

                                    Request.Pending(this.assistantSearchRequestId, ApiCatalog.FindCatalogs(queryParams))
                                        .onSuccess((res) => {
                                            if (!res || !res.list || res.list.length === 0) {
                                                resolve("No catalogs found matching the specified criteria in the connector database.");
                                                return;
                                            }

                                            const uniqueCatalogs = [];
                                            const seenIds = new Set();
                                            for (const item of res.list) {
                                                if (item && item.id && !seenIds.has(item.id)) {
                                                    seenIds.add(item.id);
                                                    uniqueCatalogs.push(item);
                                                }
                                            }

                                            const localCatalogs = uniqueCatalogs.filter(
                                                (c) => c.local === true || c.local === "true" || c.local === 1,
                                            );
                                            const remoteCatalogs = uniqueCatalogs.filter(
                                                (c) => !(c.local === true || c.local === "true" || c.local === 1),
                                            );

                                            let output = `Database Catalog Search Results:\n`;
                                            output += `- Total unique catalogs found: ${uniqueCatalogs.length}\n`;
                                            output += `- Local catalogs count: ${localCatalogs.length}\n`;
                                            output += `- Remote catalogs count: ${remoteCatalogs.length}\n\n`;

                                            if (localCatalogs.length > 0) {
                                                output += `### LIST OF LOCAL CATALOGS (${localCatalogs.length}):\n`;
                                                output += localCatalogs
                                                    .map((catalog) => {
                                                        return (
                                                            `- **ID:** \`${catalog.id}\`\n` +
                                                            `  **Title:** ${catalog.title}\n` +
                                                            `  **Keywords:** ${catalog.keywords?.join(", ") || "None"}\n` +
                                                            `  **Themes:** ${catalog.themes?.join(", ") || "None"}\n` +
                                                            `  **Creator:** ${catalog.creator || "Unknown"}\n` +
                                                            `  **Homepage:** ${catalog.homepage || "N/A"}\n` +
                                                            `  **Description:** ${catalog.description || "No description available."}`
                                                        );
                                                    })
                                                    .join("\n\n");
                                                output += "\n\n";
                                            }

                                            if (remoteCatalogs.length > 0) {
                                                output += `### LIST OF REMOTE CATALOGS (${remoteCatalogs.length}):\n`;
                                                output += remoteCatalogs
                                                    .map((catalog) => {
                                                        const provider = catalog.participant?.name
                                                            ? `${catalog.participant.name} (${catalog.participant.id})`
                                                            : catalog.participant?.id || "Unknown";
                                                        return (
                                                            `- **ID:** \`${catalog.id}\`\n` +
                                                            `  **Title:** ${catalog.title}\n` +
                                                            `  **Provider Participant:** ${provider}\n` +
                                                            `  **Keywords:** ${catalog.keywords?.join(", ") || "None"}\n` +
                                                            `  **Themes:** ${catalog.themes?.join(", ") || "None"}\n` +
                                                            `  **Creator:** ${catalog.creator || "Unknown"}\n` +
                                                            `  **Description:** ${catalog.description || "No description available."}`
                                                        );
                                                    })
                                                    .join("\n\n");
                                            }

                                            if (res.continuationToken) {
                                                output += `\n\n*(Note: There are more items available. Continuation token: ${res.continuationToken})*`;
                                            }

                                            resolve(output);
                                        })
                                        .onRequestError((err, handleError) => {
                                            handleError(err, {
                                                unauthorized: () => reject(new Error("API error: Unauthorized access to catalogs.")),
                                                forbidden: () => reject(new Error("API error: Access denied. Missing permission.")),
                                                serverError: () => reject(new Error("API error: Internal server error.")),
                                                temporalError: () => reject(new Error("API error: Temporary connection problem.")),
                                                networkError: () => reject(new Error("Network error: Could not reach the server.")),
                                            });
                                        })
                                        .onUnexpectedError((err) => {
                                            reject(err);
                                        });
                                });
                            },
                        },
                        {
                            name: "ListSources",
                            description:
                                "Retrieves the list of data sources configured in the connector database. " +
                                "Use it when the user asks about available data sources, integrations, flows, or connection types.",
                            parameters: [{ name: "nameFilter", description: "Optional text to filter the retrieved sources by name." }],
                            invoke: async (call) => {
                                const nameFilter = (getToolArgument(call.arguments, ["nameFilter"]) || "").toString().toLowerCase();

                                return new Promise<string>((resolve, reject) => {
                                    const queryParams: any = { limit: 100 };

                                    Request.Pending(this.assistantSearchRequestId, ApiSources.ListSources(queryParams))
                                        .onSuccess((res) => {
                                            if (!res || !res.list || res.list.length === 0) {
                                                resolve("No data sources found in the connector database.");
                                                return;
                                            }

                                            let filteredSources = res.list;
                                            if (nameFilter) {
                                                filteredSources = filteredSources.filter(
                                                    (source) => source.name && source.name.toLowerCase().includes(nameFilter),
                                                );
                                            }

                                            if (filteredSources.length === 0) {
                                                resolve(`No data sources found matching the name filter: '${nameFilter}'.`);
                                                return;
                                            }

                                            let output = `Database Sources Search Results:\n`;
                                            output += `- Total sources retrieved matching criteria: ${filteredSources.length}\n\n`;

                                            output += `### LIST OF DATA SOURCES:\n`;
                                            output += filteredSources
                                                .map((source, idx) => {
                                                    const createdDate = new Date(source.created).toLocaleDateString();
                                                    const modifiedDate = new Date(source.lastModified).toLocaleDateString();

                                                    return (
                                                        `${idx + 1}. **ID:** \`${source.id}\`\n` +
                                                        `   **Name:** ${source.name}\n` +
                                                        `   **Flow Type:** ${source.dataFlowType}\n` +
                                                        `   **Connection Type:** ${source.connectionType}\n` +
                                                        `   **Created:** ${createdDate} (Last modified: ${modifiedDate})`
                                                    );
                                                })
                                                .join("\n\n");

                                            if (res.continuationToken) {
                                                output += `\n\n*(Note: There are more sources available in the database that were not retrieved. Continuation token: ${res.continuationToken})*`;
                                            }

                                            resolve(output);
                                        })
                                        .onRequestError((err, handleError) => {
                                            handleError(err, {
                                                unauthorized: () => reject(new Error("API error: Unauthorized access to data sources.")),
                                                forbidden: () => reject(new Error("API error: Access denied. Missing permission.")),
                                                serverError: () => reject(new Error("API error: Internal server error.")),
                                                temporalError: () => reject(new Error("API error: Temporary connection problem.")),
                                                networkError: () => reject(new Error("Network error: Could not reach the server.")),
                                            });
                                        })
                                        .onUnexpectedError((err) => {
                                            reject(err);
                                        });
                                });
                            },
                        },
                        {
                            name: "ListSinks",
                            description:
                                "Retrieves the list of data sinks configured in the connector database. " +
                                "Use it when the user asks about available data sinks, destinations, data exports, flows, or connection types.",
                            parameters: [{ name: "nameFilter", description: "Optional text to filter the retrieved sinks by name." }],
                            invoke: async (call) => {
                                const nameFilter = (getToolArgument(call.arguments, ["nameFilter"]) || "").toString().toLowerCase();

                                return new Promise<string>((resolve, reject) => {
                                    const queryParams: any = { limit: 100 };

                                    Request.Pending(this.assistantSearchRequestId, ApiSinks.ListSinks(queryParams))
                                        .onSuccess((res) => {
                                            if (!res || !res.list || res.list.length === 0) {
                                                resolve("No data sinks found in the connector database.");
                                                return;
                                            }

                                            let filteredSinks = res.list;
                                            if (nameFilter) {
                                                filteredSinks = filteredSinks.filter(
                                                    (sink) => sink.name && sink.name.toLowerCase().includes(nameFilter),
                                                );
                                            }

                                            if (filteredSinks.length === 0) {
                                                resolve(`No data sinks found matching the name filter: '${nameFilter}'.`);
                                                return;
                                            }

                                            let output = `Database Sinks Search Results:\n`;
                                            output += `- Total sinks retrieved matching criteria: ${filteredSinks.length}\n\n`;

                                            output += `### LIST OF DATA SINKS:\n`;
                                            output += filteredSinks
                                                .map((sink, idx) => {
                                                    const createdDate = new Date(sink.created).toLocaleDateString();
                                                    const modifiedDate = new Date(sink.lastModified).toLocaleDateString();

                                                    return (
                                                        `${idx + 1}. **ID:** \`${sink.id}\`\n` +
                                                        `   **Name:** ${sink.name}\n` +
                                                        `   **Flow Type:** ${sink.dataFlowType}\n` +
                                                        `   **Connection Type:** ${sink.connectionType}\n` +
                                                        `   **Created:** ${createdDate} (Last modified: ${modifiedDate})`
                                                    );
                                                })
                                                .join("\n\n");

                                            if (res.continuationToken) {
                                                output += `\n\n*(Note: There are more sinks available in the database that were not retrieved. Continuation token: ${res.continuationToken})*`;
                                            }

                                            resolve(output);
                                        })
                                        .onRequestError((err, handleError) => {
                                            handleError(err, {
                                                unauthorized: () => reject(new Error("API error: Unauthorized access to data sinks.")),
                                                forbidden: () => reject(new Error("API error: Access denied. Missing permission.")),
                                                serverError: () => reject(new Error("API error: Internal server error.")),
                                                temporalError: () => reject(new Error("API error: Temporary connection problem.")),
                                                networkError: () => reject(new Error("Network error: Could not reach the server.")),
                                            });
                                        })
                                        .onUnexpectedError((err) => {
                                            reject(err);
                                        });
                                });
                            },
                        },
                    ],
                };
            }),
            999999, // Max priority
        );
    },
    beforeUnmount: function () {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        Request.Abort(this.rulebookInstructionsRequestId);
    },
    watch: {
        $route: function () {
            this.onRouteChanged();
        },
    },
});
</script>
